import { useCallback, MouseEvent } from 'react'
import { Stack } from '@mui/material'
import { getDay } from 'date-fns/esm'

import { Day } from '../Day'
import { EmptyDay } from '../EmptyDay'

import type {
  CalendarClickType,
  WeekCalendarPublicProps as Props,
  WeeklyValidSelection,
} from '../../../typings'
import { cardSize, cardTopSpace } from '../../constants'
import { parseDate } from '../../../utils'

import { useSelectionData } from './useSelectionData'
import { useWeeklyDays } from './useWeeklyDays'

type DayDataset = {
  day: string
  resetSelection?: string
}

const rowHeight = cardSize + cardTopSpace
const sixWeeksCalendarHeight = rowHeight * 6

export function Calendar(props: Props) {
  const { highlightedDays, onChange, selection, allowAutoMonthHeight, dotColor } = props

  const selectionData = useSelectionData(selection)
  const minHeight = allowAutoMonthHeight ? 'auto' : sixWeeksCalendarHeight

  const days = useWeeklyDays({
    selection: selectionData,
    highlightedDays,
  })

  const handleOnClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (e.target && !(e.target as HTMLElement).matches('button.o-calendar__card')) {
        return
      }

      const dataset = (e.target as HTMLButtonElement).dataset as DayDataset
      const { day, resetSelection = '0' } = dataset

      if (!day) {
        if (process.env.NODE_ENV === 'development') {
          throw new Error('Invalid day dataset')
        }
        return
      }

      const type: CalendarClickType = e.type === 'dblclick' ? 'dblclick' : 'click'
      const targetDay = resetSelection === '1' ? undefined : day

      if (targetDay) {
        const dayDate = getDay(parseDate(targetDay)) as WeeklyValidSelection
        onChange(dayDate, selection, type)
      }
    },
    [onChange, selection],
  )

  return (
    <Stack direction="column" height="auto" alignItems="center" flexShrink={0} mt={3} mb={4}>
      <Stack
        direction="row"
        flexWrap="wrap"
        width="100%"
        minHeight={minHeight}
        alignContent="flex-start"
        justifyContent="space-between"
        onClick={handleOnClick}
        onDoubleClick={handleOnClick}
      >
        {days.map((day, index) =>
          day.type === 'empty' ? (
            <EmptyDay firstOfRow={day.firstOfRow} key={index} />
          ) : (
            <Day {...day} key={day.id} dotColor={dotColor} />
          ),
        )}
      </Stack>
    </Stack>
  )
}
